// eslint-disable-next-line no-unused-vars
import React, { useState, useMemo } from 'react';
import { number, object, string } from 'prop-types';
import { useTranslation } from 'react-i18next';

import useCart from 'Hooks/useCart';
import useToastNotifications from 'Hooks/useToastNotifications';
import CartPromotionSuggestionsComponent from 'Components/Products/CartPromotionSuggestions';

const composePromotions = product => ({
  ...product,
  promotionId: product?.promotionId || product.erpPromotionId,
  availablePromotions: (product.availablePromotions || []).map(promotion => ({
    ...promotion,
    products: (promotion.products || []).sort((a, b) => b.isMainProduct - a.isMainProduct),
    ...(promotion.products || []).reduce((prices, prod) => {
      // eslint-disable-next-line no-param-reassign
      prices.totalPrice += prod.ronPrice * prod.quantity;
      // eslint-disable-next-line no-param-reassign
      prices.totalPromoPrice += prod.ronTotalPrice;
      return prices;
    }, { totalPrice: 0, totalPromoPrice: 0 })
  }))
});

const CartPromotionSuggestions = ({
  colSpan,
  product,
  theme,
  currency,
}) => {
  const cart = useCart();
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const [promoAddToCartLoading, setPromoAddToCartLoading] = useState(false);
  const updatedProduct = useMemo(() => composePromotions(product), [product]);

  const handleOnAddPromoToCart = async promoProduct => {
    const { quantity } = promoProduct;
    const id = product.id || product.productId;
    const promotionId = promoProduct.promotionId || promoProduct.promotion?.promotionId;
    const payload = {
      id,
      stockValue: promoProduct.stockValue,
      quantity,
      minQuantity: promoProduct.minQuantity,
      promotion: {
        promotionId
      }
    };
    if (payload.quantity > promoProduct.stockValue) {
      toast.addNotification({
        type: 'warning',
        title: t('products.insufficientStock')
      });
      return null;
    }
    try {
      setPromoAddToCartLoading(promotionId);
      const response = await cart.updateProduct(payload);
      toast.addNotification({
        title: t('cart.successfullyAddedPromotionToCart')
      });
      return response;
    } catch (err) {
      toast.addNotification({
        title: t('cart.failedAddingPromotionToCart'),
        type: 'error'
      });
    } finally {
      setPromoAddToCartLoading(false);
    }
    return null;
  };

  return (
    <CartPromotionSuggestionsComponent
      colSpan={ colSpan }
      product={ updatedProduct }
      theme={ theme }
      loading={ promoAddToCartLoading }
      onAddPromoToCart={ handleOnAddPromoToCart }
      currency={ currency }
    />
  );
};

CartPromotionSuggestions.propTypes = {
  colSpan: number.isRequired,
  product: object.isRequired,
  theme: object.isRequired,
  currency: string.isRequired,
};

export default CartPromotionSuggestions;
